import * as React from "react";
import UserInfoDto = Cloud.RavenDB.Core.Dto.Users.UserInfoDto;
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import LoginStage = Cloud.RavenDB.Core.Dto.TwoFactorAuth.LoginStage;
import LoginWith2faDto = Cloud.RavenDB.Core.Dto.TwoFactorAuth.LoginWith2faDto;
import LoginDto = Cloud.RavenDB.Core.Dto.Users.LoginDto;
import { stripCloudAccountName } from "../AppRoutes";
import { useRouter } from "@commonClient/src/hooks/useRouter";
import { Loading } from "@commonComponents/ui/Loading";
import CloudAccountInfoDto = Cloud.RavenDB.Core.Dto.Users.CloudAccountInfoDto;
import { useServices } from "./useServices";

interface UserContextDto {
    cloudAccount: string;
    userInfo: UserInfoDto;
    login2fa: (code: LoginWith2faDto) => Promise<void>;
    checkLoginStage: () => Promise<LoginStage>;
    logout: () => Promise<void>;
    login: (dto: LoginDto) => Promise<void>;
    reloadUserInfo: () => Promise<void>;
    setCurrentCloudAccountName: (cloudAccount: string) => void;
    matchingCloudAccount: CloudAccountInfoDto;
    hipaa: boolean;
}

const authContext = createContext<UserContextDto>(undefined);

export function ProvideAuth({ children }) {
    const auth = useProvideAuth();
    if (auth.userInfo) {
        return <authContext.Provider value={auth}>{children}</authContext.Provider>;
    } else {
        return <Loading />;
    }
}

export const useAuth = () => {
    return useContext(authContext);
};

function useProvideAuth(): UserContextDto {
    const { userService, authenticationService } = useServices();
    const { location, push } = useRouter();
    const [userInfo, setUserInfo] = useState<UserInfoDto>();
    const [cloudAccount, setCloudAccount] = useState<string>(undefined);

    const matchingCloudAccount = userInfo?.isAuthenticated
        ? userInfo.cloudAccounts.find((x) => x.domainName === cloudAccount)
        : null;

    const login2fa = async (code: LoginWith2faDto) => {
        await authenticationService.loginWith2fa(code);
        await reloadUserInfo();
    };

    const checkLoginStage = useCallback(() => {
        return authenticationService.checkLoginStage();
    }, [authenticationService]);

    const reloadUserInfo = useCallback(async () => {
        const userInfo = await userService.getUserInfo();

        if (userInfo.isAuthenticated) {
            const matchingAccount = userInfo.cloudAccounts.find((x) => x.domainName === cloudAccount);

            if (!matchingAccount) {
                localStorage.setItem(
                    "X-Tenant-ID",
                    userInfo.cloudAccounts.length ? userInfo.cloudAccounts[0].domainName : undefined
                );
                setCloudAccount(userInfo.cloudAccounts.length ? userInfo.cloudAccounts[0].domainName : null);
            }
        } else {
            setCloudAccount(null);
        }

        setUserInfo(userInfo);
    }, [userService, cloudAccount]);

    const logout = async () => {
        await userService.logout();
        localStorage.removeItem("X-Tenant-ID");
        await reloadUserInfo();
    };

    const login = async (dto: LoginDto) => {
        await userService.requestLogin(dto);
    };

    const setCurrentCloudAccountName = (cloudAccount: string) => {
        const path = location.pathname;
        setCloudAccount(cloudAccount);

        localStorage.setItem("X-Tenant-ID", cloudAccount);

        if (path.startsWith("/portal/") && !path.startsWith("/portal/profile")) {
            const relativePath = stripCloudAccountName(path);
            push("/portal/" + cloudAccount + relativePath);
        }
    };

    useEffect(() => {
        reloadUserInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        userInfo,
        cloudAccount,
        login2fa,
        checkLoginStage,
        logout,
        login,
        hipaa: matchingCloudAccount && matchingCloudAccount.accountType === "Hipaa",
        reloadUserInfo,
        setCurrentCloudAccountName,
        matchingCloudAccount,
    };
}
